import React, { useState } from "react";
import { Copy, ShieldCheck, Activity, Info, CheckCircle2, Users, FileText, Sparkles, TrendingUp, IndianRupee } from "lucide-react";
import SEO from "../components/SEO";

export default function DesignSystem() {
    const [copiedColor, setCopiedColor] = useState(null);
    const [chatInput, setChatInput] = useState("");
    const [chatMessages, setChatMessages] = useState([
        { sender: "assistant", text: "Hello! I am your AI Business Automation assistant. I've automatically reconciled 47 invoices today. Do you want to process the GST Q2 summary?" }
    ]);
    const [activeDashboardTab, setActiveDashboardTab] = useState("overview");

    const copyToClipboard = (hex) => {
        navigator.clipboard.writeText(hex);
        setCopiedColor(hex);
        setTimeout(() => setCopiedColor(null), 2000);
    };

    const handleSendMessage = (e) => {
        e.preventDefault();
        if (!chatInput.trim()) return;
        
        const newMsg = { sender: "user", text: chatInput };
        setChatMessages(prev => [...prev, newMsg]);
        setChatInput("");

        setTimeout(() => {
            setChatMessages(prev => [
                ...prev,
                { 
                    sender: "assistant", 
                    text: `Based on your request, I have updated the automation pipeline. The billing ledger and inventory registers are fully synchronized. All compliance requirements are verified.`
                }
            ]);
        }, 1000);
    };

    const colors = [
        {
            name: "Primary - Deep Trust Blue",
            hex: "#0F4C81",
            role: "Enterprise Trust & Integrity",
            description: "Main branding, headers, secure layout structures, and core actions. Conveying stability, bank-grade operations, and enterprise resilience.",
            variable: "var(--color-brand-primary)"
        },
        {
            name: "Secondary - Emerald Green",
            hex: "#16A34A",
            role: "Finance & Financial Growth",
            description: "Used to highlight growth analytics, GST calculations, successful payroll automation, invoices paid, and positive financial trajectories.",
            variable: "var(--color-brand-secondary)"
        },
        {
            name: "Accent - Technology Cyan",
            hex: "#06B6D4",
            role: "AI, Innovation & Speed",
            description: "A dynamic tech-driven color representing AI triggers, real-time sync status, digital ledger verification, and custom automation tags.",
            variable: "var(--color-brand-tech)"
        },
        {
            name: "Base Neutrals - Light Mode",
            hex: "#F8FAFC",
            role: "Interface Canvas Background",
            description: "Clean Slate 50 background to prevent visual fatigue, allowing elements to pop. Paired with pure white (#FFFFFF) for card surfaces.",
            variable: "var(--bg-base)"
        },
        {
            name: "Base Neutrals - Dark Mode",
            hex: "#0B0F19",
            role: "Midnight Vault Background",
            description: "A premium midnight navy-slate tone rather than plain black. Retains technological depth and ensures high-contrast readability.",
            variable: "var(--bg-base) (dark)"
        }
    ];

    const accessibilityPairings = [
        { text: "White Text on Deep Trust Blue (#0F4C81)", ratio: "5.1:1", status: "PASS (AA)", detail: "Excellent for primary headers and primary buttons." },
        { text: "White Text on Emerald Green (#16A34A)", ratio: "4.5:1", status: "PASS (AA)", detail: "Passes WCAG requirements for headers and status tags." },
        { text: "Primary Slate Text (#0F172A) on Base Gray (#F8FAFC)", ratio: "19.1:1", status: "PASS (AAA)", detail: "Perfect compliance for paragraph text and body reads." },
        { text: "Lighter Blue (#38BDF8) on Midnight Slate (#111827)", ratio: "9.8:1", status: "PASS (AAA)", detail: "Extremely crisp dark mode branding contrast." },
        { text: "Lighter Emerald (#34D399) on Midnight Slate (#111827)", ratio: "8.5:1", status: "PASS (AAA)", detail: "Optimal readability for success logs in dark view." },
        { text: "Lighter Cyan (#22D3EE) on Midnight Slate (#111827)", ratio: "9.5:1", status: "PASS (AAA)", detail: "Exceptional contrast for high-tech badges in dark mode." }
    ];

    return (
        <div className="w-full relative overflow-visible">
            <SEO
                title="Interactive Color Palette & Design System | blinqbooks"
                description="Explore our secure, finance-focused B2B ERP design system. Premium Deep Blue, Emerald Green, and Technology Cyan palettes designed for enterprise compliance."
            />

            {/* Organic Floating Background Blur Blobs to soften the layout */}
            <div className="absolute top-[10%] left-[-15%] w-[400px] h-[400px] rounded-full bg-blue-400/15 dark:bg-blue-900/10 blur-[120px] pointer-events-none -z-10 animate-pulse"></div>
            <div className="absolute top-[40%] right-[-15%] w-[450px] h-[450px] rounded-full bg-cyan-400/10 dark:bg-cyan-900/8 blur-[130px] pointer-events-none -z-10"></div>
            <div className="absolute bottom-[10%] left-[20%] w-[350px] h-[350px] rounded-full bg-green-400/10 dark:bg-green-900/5 blur-[100px] pointer-events-none -z-10"></div>

            {/* Header section */}
            <div className="mb-16 relative">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50/70 dark:bg-blue-950/30 text-xs font-semibold text-brand-primary dark:text-[#38BDF8] tracking-wider uppercase mb-4 backdrop-blur-sm">
                    <Sparkles className="w-3.5 h-3.5 text-brand-tech animate-pulse" />
                    Interactive Palette Review
                </div>
                <h1 className="text-4xl md:text-6xl font-black text-primary tracking-tight mb-4">
                    Enterprise Design System
                </h1>
                <p className="text-lg md:text-xl text-secondary max-w-3xl leading-relaxed">
                    Designed to communicate trust, financial growth, and technology automation. 
                    This layout provides color codes, contrast audits, interactive buttons, and a live visual ERP mock.
                </p>
            </div>

            {/* Part 1: Color Palette Cards (Flat layout with no layered cardboard) */}
            <section className="mb-20">
                <h2 className="text-2xl font-extrabold text-primary mb-8 flex items-center gap-3 pb-3">
                    <span className="w-2.5 h-8 rounded-full bg-brand-primary"></span>
                    1. Brand Palette & Swatches
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {colors.map((c, i) => (
                        <div key={i} className="relative group transition-all duration-300">
                            <div className="p-1">
                                <div 
                                    className="h-36 w-full relative rounded-3xl overflow-hidden shadow-inner" 
                                    style={{ backgroundColor: c.hex }}
                                >
                                    {/* Glassmorphic hex tag */}
                                    <button 
                                        onClick={() => copyToClipboard(c.hex)}
                                        className="absolute bottom-3 right-3 px-3.5 py-2 rounded-full bg-white/90 dark:bg-slate-900/90 text-slate-800 dark:text-white text-xs font-bold shadow-md hover:bg-white active:scale-95 transition-all flex items-center gap-1.5 backdrop-blur-sm"
                                    >
                                        {copiedColor === c.hex ? (
                                            <>
                                                <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                                                Copied
                                            </>
                                        ) : (
                                            <>
                                                <Copy className="w-3.5 h-3.5 text-slate-400" />
                                                {c.hex}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div className="px-3 pb-8 pt-4">
                                <span className="text-[10px] tracking-widest uppercase font-bold text-brand-secondary mb-1 block">
                                    {c.role}
                                </span>
                                <h3 className="text-xl font-extrabold text-primary mb-2">{c.name}</h3>
                                <p className="text-sm text-secondary mb-4 leading-relaxed h-16 overflow-y-auto">
                                    {c.description}
                                </p>
                                <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800/40 flex items-center justify-between text-xs font-mono text-slate-500 dark:text-slate-400">
                                    <span>{c.hex}</span>
                                    <span className="text-slate-300 dark:text-slate-700">•</span>
                                    <span>{c.variable}</span>
                                    <button 
                                        onClick={() => copyToClipboard(c.hex)}
                                        className="text-blue-500 hover:underline font-bold font-sans text-[11px]"
                                    >
                                        Copy Hex
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Part 2: Interactive Mock Dashboard */}
            <section className="mb-20">
                <h2 className="text-2xl font-extrabold text-primary mb-8 flex items-center gap-3 pb-3">
                    <span className="w-2.5 h-8 rounded-full bg-brand-secondary"></span>
                    2. Live ERP & Accounting Mockup
                </h2>

                <div className="bg-[#F8FAFC] dark:bg-slate-950/70 p-4 md:p-8 rounded-[2.5rem] backdrop-blur-xl">
                    {/* Mock Browser Header with soft rounding */}
                    <div className="flex items-center justify-between bg-white dark:bg-slate-900 px-6 py-4 rounded-t-[1.75rem]">
                        <div className="flex items-center gap-3">
                            <div className="flex gap-2">
                                <span className="w-3.5 h-3.5 rounded-full bg-red-400/90 shadow-sm"></span>
                                <span className="w-3.5 h-3.5 rounded-full bg-yellow-400/90 shadow-sm"></span>
                                <span className="w-3.5 h-3.5 rounded-full bg-green-400/90 shadow-sm"></span>
                            </div>
                            <span className="text-xs font-bold text-slate-400 dark:text-slate-500 bg-[#F8FAFC] dark:bg-slate-950 px-4 py-1.5 rounded-full">
                                blinqbooks.com/dashboard/gst-payroll
                            </span>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="w-2 h-2 rounded-full bg-green-500 animate-ping"></span>
                            <span className="text-[10px] font-bold text-green-600 dark:text-green-400 uppercase tracking-widest">AI Synced</span>
                        </div>
                    </div>

                    {/* Dashboard Body */}
                    <div className="bg-white/80 dark:bg-slate-900/80 p-4 md:p-6 rounded-b-[1.75rem] grid grid-cols-1 lg:grid-cols-12 gap-6">
                        
                        {/* Sidebar (with fluid design) */}
                        <div className="lg:col-span-3 space-y-2 lg:pr-6">
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-3 mb-4">ERP Modules</div>
                            {[
                                { id: "overview", label: "Financial Overview", count: null },
                                { id: "gst", label: "GST & Tax Filing", count: "Reconciled" },
                                { id: "payroll", label: "Payroll Automations", count: "12 Staff" },
                                { id: "inventory", label: "Inventory Sync", count: "Active" }
                            ].map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveDashboardTab(tab.id)}
                                    className={`w-full text-left px-5 py-3 rounded-full font-bold text-sm transition-all duration-300 flex items-center justify-between
                                        ${activeDashboardTab === tab.id 
                                            ? "bg-brand-primary text-white shadow-lg shadow-blue-900/10" 
                                            : "hover:bg-slate-50 dark:hover:bg-slate-800/60 text-slate-600 dark:text-slate-400"
                                        }
                                    `}
                                >
                                    <span>{tab.label}</span>
                                    {tab.count && (
                                        <span className={`text-[10px] font-extrabold px-2.5 py-0.5 rounded-full transition-colors duration-300
                                            ${activeDashboardTab === tab.id 
                                                ? "bg-white/20 text-white" 
                                                : "bg-slate-50 dark:bg-slate-800 text-slate-550"
                                            }
                                        `}>
                                            {tab.count}
                                        </span>
                                    )}
                                </button>
                            ))}

                            <div className="pt-6 mt-6">
                                <div className="p-5 rounded-[1.75rem] bg-[#F8FAFC] dark:bg-[#0B0F19]/40">
                                    <div className="flex items-center gap-2 text-brand-primary dark:text-[#38BDF8] mb-1.5">
                                        <Sparkles className="w-4 h-4 text-brand-tech animate-pulse" />
                                        <span className="text-xs font-extrabold uppercase tracking-wider">Enterprise AI Tier</span>
                                    </div>
                                    <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed font-medium">Your business accounts are continuously audited by blinqbooks agents.</p>
                                </div>
                            </div>
                        </div>

                        {/* Main Grid View */}
                        <div className="lg:col-span-9 space-y-6">
                            {/* Analytics flat containers */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div className="bg-[#F8FAFC] dark:bg-[#0B0F19]/60 p-6 rounded-3xl hover:scale-[1.01] transition-all duration-300">
                                    <div className="flex items-center justify-between mb-3 text-slate-400">
                                        <span className="text-xs font-bold uppercase tracking-wider">Operating Revenue</span>
                                        <IndianRupee className="w-4.5 h-4.5 text-brand-primary" />
                                    </div>
                                    <div className="text-2xl font-black text-primary">₹48,250.00</div>
                                    <div className="flex items-center gap-1.5 text-[11px] font-bold text-green-600 dark:text-green-400 mt-2">
                                        <TrendingUp className="w-4.5 h-4.5" />
                                        <span>+18.4% this month</span>
                                    </div>
                                </div>

                                <div className="bg-[#F8FAFC] dark:bg-[#0B0F19]/60 p-6 rounded-3xl hover:scale-[1.01] transition-all duration-300">
                                    <div className="flex items-center justify-between mb-3 text-slate-400">
                                        <span className="text-xs font-bold uppercase tracking-wider">GST Liability Q2</span>
                                        <FileText className="w-4.5 h-4.5 text-brand-secondary" />
                                    </div>
                                    <div className="text-2xl font-black text-primary">₹8,190.20</div>
                                    <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500 mt-2">
                                        <CheckCircle2 className="w-4 h-4 text-brand-secondary" />
                                        <span>Fully Reconciled</span>
                                    </div>
                                </div>

                                <div className="bg-[#F8FAFC] dark:bg-[#0B0F19]/60 p-6 rounded-3xl hover:scale-[1.01] transition-all duration-300">
                                    <div className="flex items-center justify-between mb-3 text-slate-400">
                                        <span className="text-xs font-bold uppercase tracking-wider">Active Employees</span>
                                        <Users className="w-4.5 h-4.5 text-brand-tech" />
                                    </div>
                                    <div className="text-2xl font-black text-primary">12 / 12</div>
                                    <div className="flex items-center gap-1.5 text-[11px] font-bold text-green-600 dark:text-[#22D3EE] mt-2">
                                        <CheckCircle2 className="w-4 h-4 text-brand-tech" />
                                        <span>Payroll processed</span>
                                    </div>
                                </div>
                            </div>

                            {/* Chart representation */}
                            <div className="p-6 bg-[#F8FAFC] dark:bg-[#0B0F19]/60 rounded-3xl">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                                    <div>
                                        <h4 className="font-extrabold text-sm text-primary uppercase tracking-wider">Operating Expenses vs Cashflow</h4>
                                        <p className="text-xs text-secondary">Real-time dynamic billing cycles</p>
                                    </div>
                                    <div className="flex flex-wrap gap-4 text-xs font-semibold">
                                        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-brand-primary"></span> Revenue</span>
                                        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-brand-secondary"></span> Taxes</span>
                                        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-brand-tech"></span> AI Reconciliations</span>
                                    </div>
                                </div>

                                {/* Mock SVG Graph with round boundaries */}
                                <div className="h-44 w-full bg-white dark:bg-slate-950/60 rounded-2xl relative overflow-hidden flex items-end px-2">
                                    {/* Grid Lines */}
                                    <div className="absolute inset-x-0 top-1/4 border-b border-dashed border-slate-200/30 dark:border-slate-800/30"></div>
                                    <div className="absolute inset-x-0 top-2/4 border-b border-dashed border-slate-200/30 dark:border-slate-800/30"></div>
                                    <div className="absolute inset-x-0 top-3/4 border-b border-dashed border-slate-200/30 dark:border-slate-800/30"></div>

                                    {/* Mock curves */}
                                    <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
                                        <defs>
                                            <linearGradient id="blueGlow" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#0F4C81" stopOpacity="0.15" />
                                                <stop offset="100%" stopColor="#0F4C81" stopOpacity="0" />
                                            </linearGradient>
                                            <linearGradient id="cyanGlow" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#06B6D4" stopOpacity="0.2" />
                                                <stop offset="100%" stopColor="#06B6D4" stopOpacity="0" />
                                            </linearGradient>
                                        </defs>
                                        {/* Filled curves */}
                                        <path d="M 0 140 Q 150 90 300 110 T 600 50 T 900 30 L 900 180 L 0 180 Z" fill="url(#blueGlow)" />
                                        <path d="M 0 150 Q 200 130 400 90 T 800 60 L 900 50 L 900 180 L 0 180 Z" fill="url(#cyanGlow)" />
                                        
                                        {/* Colored Lines */}
                                        <path d="M 0 140 Q 150 90 300 110 T 600 50 T 900 30" fill="none" stroke="var(--color-brand-primary)" strokeWidth="3" strokeLinecap="round" />
                                        <path d="M 0 150 Q 200 130 400 90 T 800 60 L 900 50" fill="none" stroke="var(--color-brand-tech)" strokeWidth="2.5" strokeDasharray="6,4" />
                                        <path d="M 0 170 Q 250 160 500 140 T 900 120" fill="none" stroke="var(--color-brand-secondary)" strokeWidth="2" />
                                    </svg>

                                    {/* Data Labels (Pill-shaped) */}
                                    <div className="absolute left-4 top-4 bg-[#F8FAFC] dark:bg-slate-800/95 shadow-md p-2.5 rounded-2xl text-[10px] pointer-events-none">
                                        <span className="font-extrabold text-slate-400 uppercase">Interactive Peak</span>
                                        <div className="text-xs font-bold text-primary">₹48.2k Cash Inflow</div>
                                    </div>
                                </div>
                            </div>

                            {/* bottom two elements (AI assistant + Activity tracker) */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* AI Assistant Chat (Fluid rounded bubbles, borderless) */}
                                <div className="bg-[#F8FAFC] dark:bg-[#0B0F19]/60 p-6 rounded-3xl flex flex-col justify-between">
                                    <div className="flex items-center gap-2 mb-4 pb-3">
                                        <div className="p-2 rounded-xl bg-brand-tech/15 text-brand-tech">
                                            <Sparkles className="w-4 h-4 animate-pulse" />
                                        </div>
                                        <div>
                                            <h4 className="font-extrabold text-xs text-primary uppercase tracking-wider">AI Business Automation</h4>
                                            <p className="text-[10px] text-green-500 font-bold">Active in background</p>
                                        </div>
                                    </div>

                                    {/* Chat dialogue bubbles (Soft rounded asymmetric shape) */}
                                    <div className="space-y-3 h-32 overflow-y-auto mb-4 p-2 bg-white dark:bg-slate-950/40 rounded-2xl text-[12px] leading-relaxed">
                                        {chatMessages.map((m, index) => (
                                            <div key={index} className={`flex flex-col ${m.sender === "user" ? "items-end" : "items-start"}`}>
                                                <div className={`px-4 py-2.5 font-medium
                                                    ${m.sender === "user" 
                                                        ? "bg-brand-primary text-white rounded-2xl rounded-tr-none shadow-md shadow-blue-900/10" 
                                                        : "bg-[#F8FAFC] dark:bg-slate-800 text-secondary rounded-2xl rounded-tl-none"
                                                    }
                                                `}>
                                                    {m.text}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Message input */}
                                    <form onSubmit={handleSendMessage} className="relative flex items-center">
                                        <input
                                            type="text"
                                            value={chatInput}
                                            onChange={(e) => setChatInput(e.target.value)}
                                            placeholder="Ask AI to draft GST Q2 report..."
                                            className="w-full pl-4 pr-16 py-3 bg-white dark:bg-slate-800 text-xs rounded-full outline-none text-slate-800 dark:text-white"
                                        />
                                        <button 
                                            type="submit" 
                                            className="absolute right-1.5 px-4 py-1.5 bg-brand-primary hover:bg-brand-primary/95 text-white text-[10px] font-bold rounded-full active:scale-95 transition-all shadow-md shadow-blue-900/10"
                                        >
                                            Send
                                        </button>
                                    </form>
                                </div>

                                {/* Security / Compliance Status */}
                                <div className="bg-[#F8FAFC] dark:bg-[#0B0F19]/60 p-6 rounded-3xl">
                                    <h4 className="font-extrabold text-xs text-primary uppercase tracking-wider mb-4 pb-3">Compliance & Auditing</h4>
                                    
                                    <div className="space-y-4">
                                        {[
                                            { title: "GST-1 Invoicing Reconciliation", desc: "Automated ledger checking with tax validations.", done: true },
                                            { title: "TDS / Salary Tax Compliance", desc: "Deductions computed and validated by payroll bots.", done: true },
                                            { title: "Quarterly Audit Reports", desc: "Balance verification and transaction logs.", done: false }
                                        ].map((item, index) => (
                                            <div key={index} className="flex gap-3">
                                                <div className={`mt-0.5 p-1.5 rounded-full shrink-0 h-fit
                                                    ${item.done 
                                                        ? "bg-green-100 dark:bg-green-950/40 text-brand-secondary" 
                                                        : "bg-blue-100 dark:bg-blue-950/40 text-brand-primary"
                                                    }
                                                `}>
                                                    {item.done ? <CheckCircle2 className="w-4 h-4" /> : <Activity className="w-4 h-4 animate-pulse" />}
                                                </div>
                                                <div>
                                                    <h5 className="font-bold text-xs text-primary">{item.title}</h5>
                                                    <p className="text-[11px] text-secondary leading-normal">{item.desc}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Part 3: Accessibility & Contrast Audit (Borderless float list) */}
            <section className="mb-20">
                <h2 className="text-2xl font-extrabold text-primary mb-8 flex items-center gap-3 pb-3">
                    <span className="w-2.5 h-8 rounded-full bg-brand-tech"></span>
                    3. Accessibility & Contrast Audit
                </h2>

                <div className="card p-8">
                    <p className="text-sm text-secondary mb-8 leading-relaxed max-w-3xl">
                        To maintain compliance for government and enterprise clients (crucial for GST and payroll tools), 
                        we verify our color ratios against the **WCAG 2.1 Web Content Accessibility Guidelines**.
                    </p>

                    <div className="space-y-4">
                        {accessibilityPairings.map((p, i) => (
                            <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl bg-[#F8FAFC] dark:bg-slate-900/60 hover:scale-[1.005] transition-all duration-300">
                                <div className="space-y-1">
                                    <div className="font-extrabold text-sm text-primary">{p.text}</div>
                                    <div className="text-xs text-secondary">{p.detail}</div>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                    <span className="font-mono font-bold text-xs bg-slate-200 dark:bg-slate-800 px-3 py-1.5 rounded-full text-slate-800 dark:text-slate-200">
                                        Ratio: {p.ratio}
                                    </span>
                                    <span className={`px-3.5 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5
                                        ${p.status.includes("AAA") 
                                            ? "bg-green-50 dark:bg-green-950/20 text-brand-secondary" 
                                            : "bg-blue-50 dark:bg-blue-950/20 text-brand-primary"
                                        }
                                    `}>
                                        {p.status}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Part 4: Interactive Components */}
            <section className="mb-16">
                <h2 className="text-2xl font-extrabold text-primary mb-8 flex items-center gap-3 pb-3">
                    <span className="w-2.5 h-8 rounded-full bg-slate-400"></span>
                    4. UI Components Showcase
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Buttons Showcase */}
                    <div className="bg-[#F8FAFC] dark:bg-slate-900 rounded-[2.5rem] p-8 shadow-sm">
                        <h4 className="font-extrabold text-sm text-primary uppercase tracking-wider mb-6 pb-2">
                            CTA & Button States
                        </h4>
                        
                        <div className="space-y-5">
                            <div className="flex flex-wrap items-center gap-4">
                                <button className="px-7 py-3.5 rounded-full font-bold bg-brand-primary text-white hover:bg-brand-primary/95 transition-all shadow-md shadow-blue-500/10 active:scale-95 text-sm">
                                    Primary Blue CTA
                                </button>
                                <span className="text-xs text-slate-400 dark:text-slate-500 font-mono">rounded-full</span>
                            </div>

                            <div className="flex flex-wrap items-center gap-4">
                                <button className="px-7 py-3.5 rounded-2xl font-bold bg-brand-secondary text-white hover:bg-brand-secondary/95 transition-all shadow-md shadow-emerald-500/10 active:scale-95 text-sm">
                                    Secondary Green CTA
                                </button>
                                <span className="text-xs text-slate-400 dark:text-slate-500 font-mono">rounded-2xl</span>
                            </div>

                            <div className="flex flex-wrap items-center gap-4">
                                <button className="px-7 py-3.5 rounded-2xl font-bold bg-brand-tech text-white hover:bg-brand-tech/95 transition-all shadow-md shadow-cyan-500/10 active:scale-95 text-sm">
                                    Accent Cyan Button
                                </button>
                                <span className="text-xs text-slate-400 dark:text-slate-500 font-mono">rounded-2xl</span>
                            </div>

                            <div className="flex flex-wrap items-center gap-4">
                                <button className="px-7 py-3.5 rounded-full font-bold bg-surface hover:bg-surface-hover text-primary border border-strong transition-all shadow-sm active:scale-95 text-sm">
                                    Outline Normal Button
                                </button>
                                <span className="text-xs text-slate-400 dark:text-slate-500 font-mono">rounded-full</span>
                            </div>
                        </div>
                    </div>

                    {/* Status Badges & Tags */}
                    <div className="bg-[#F8FAFC] dark:bg-slate-900 rounded-[2.5rem] p-8 shadow-sm">
                        <h4 className="font-extrabold text-sm text-primary uppercase tracking-wider mb-6 pb-2">
                            Status Indicators
                        </h4>

                        <div className="space-y-5">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-slate-500">Security Tag</span>
                                <span className="px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-950/40 text-brand-primary text-xs font-bold flex items-center gap-1.5 shadow-sm">
                                    <ShieldCheck className="w-3.5 h-3.5" />
                                    256-bit Encrypted
                                </span>
                            </div>

                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-slate-500">Accounting / GST Paid Tag</span>
                                <span className="px-4 py-1.5 rounded-full bg-green-50 dark:bg-green-950/40 text-brand-secondary text-xs font-bold flex items-center gap-1.5 shadow-sm">
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                    GST Filed Success
                                </span>
                            </div>

                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-slate-500">Real-Time Sync Badge</span>
                                <span className="px-4 py-1.5 rounded-full bg-cyan-50 dark:bg-cyan-950/40 text-brand-tech text-xs font-bold flex items-center gap-1.5 shadow-sm">
                                    <Activity className="w-3.5 h-3.5 animate-pulse" />
                                    AI Automation Idle
                                </span>
                            </div>

                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-slate-500">System Banner</span>
                                <div className="p-4 bg-slate-50 dark:bg-[#0B0F19]/60 rounded-2xl text-xs text-secondary leading-relaxed flex items-start gap-3">
                                    <Info className="w-4 h-4 text-brand-primary shrink-0 mt-0.5" />
                                    <span>Oracle NetSuite style mappings are fully initialized for Q2 GST reports.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
